import React, { useContext, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { GlobalContext } from '../lib/GlobalVar';
import { ConsultaPermissoes } from '../lib/api';
import { ehRestrito } from '../lib/acessoTela';
import { BabitaLogo, BabitaB } from './BabitaLogo';
import Icon from './Icon';

// id (sem barra) = casa com o id retornado por ConsultaPermissoes
// Obs: "Logs & Historico" foi ocultado do menu a pedido (rota /Logs continua existindo).
const OP_ITEMS = [
    { id: 'Dashboard',     to: '/Dashboard',     label: 'Dashboard',         icon: 'dashboard' },
    { id: 'Crontab',       to: '/Crontab',       label: 'Crontab Visual',    icon: 'crontab' },
    { id: 'Internas',      to: '/Internas',      label: 'Páginas Internas',  icon: 'pages' },
    { id: 'Arquivos',      to: '/Arquivos',      label: 'Acesso a Arquivos', icon: 'files' },
    { id: 'Alertas',       to: '/Alertas',       label: 'Alertas',           icon: 'alerts' },
    { id: 'Configuracoes', to: '/Configuracoes', label: 'Configurações',     icon: 'settings' }
];

const TI_ITEMS = [
    { id: 'TI/Logins',     to: '/TI/Logins',     label: 'Logins & Usuários',  icon: 'shield' },
    { id: 'TI/Permissoes', to: '/TI/Permissoes', label: 'Permissões',         icon: 'grid' },
    { id: 'TI/PermissoesMemoria', to: '/TI/PermissoesMemoria', label: 'Permissões da Memória', icon: 'grid' },
    { id: 'TI/SessoesMemoria',    to: '/TI/SessoesMemoria',    label: 'Sessões da Memória',    icon: 'shield' },
    { id: 'TI/Bancos',     to: '/TI/Bancos',     label: 'Conexões de Banco',  icon: 'database' },
    { id: 'TI/Construtor', to: '/TI/Construtor', label: 'Construtor de API',  icon: 'wand' },
    { id: 'TI/Telegram',   to: '/TI/Telegram',   label: 'Telegram',           icon: 'telegram' },
    { id: 'TI/Monitor',    to: '/TI/Monitor',    label: 'Monitor',            icon: 'logs' }
];

export default function Sidebar({ collapsed, hovered, onHover }) {
    const { user, logout } = useContext(GlobalContext);
    const navigate = useNavigate();
    const location = useLocation();
    const isTIPath = location.pathname.startsWith('/TI/');
    const [itOpen, setItOpen] = useState(isTIPath);
    const [matriz, setMatriz] = useState(null); // null = ainda carregando

    const isTIUser = user && (user.setor === 'TI' || user.cargo === 'Administrador');
    const isAdmin  = user && user.cargo === 'Administrador';

    useEffect(() => { if (isTIPath) setItOpen(true); }, [isTIPath]);

    // [iter5/bug7] Carrega matriz; tambem re-carrega quando Permissoes dispara o evento
    // 'centralbabita:permissoes-atualizadas'. Administrador ignora a matriz.
    useEffect(() => {
        let alive = true;
        function recarregar() {
            ConsultaPermissoes({}).then(r => {
                if (!alive) return;
                if (r && !r.Error && r.matriz) setMatriz(r.matriz);
                else setMatriz({});
            });
        }
        recarregar();
        window.addEventListener('centralbabita:permissoes-atualizadas', recarregar);
        return () => {
            alive = false;
            window.removeEventListener('centralbabita:permissoes-atualizadas', recarregar);
        };
    }, []);

    // Decide se o item da sidebar deve aparecer para o usuario atual
    function podeVer(item) {
        if (isAdmin) return true;
        if (ehRestrito(user)) return item.id === 'Internas'; // conta restrita: so Paginas Internas
        // Telas TI: so para setor=TI
        if (item.id.startsWith('TI/')) return user && user.setor === 'TI';
        if (!matriz || !user || !user.setor) return true; // fail-open enquanto carrega
        const sb = matriz[user.setor];
        if (!sb) return true; // setor sem regra explicita = libera
        return sb[item.id] !== false; // explicitamente false = bloqueia
    }

    const opItemsVisiveis = OP_ITEMS.filter(podeVer);
    const tiItemsVisiveis = isTIUser ? TI_ITEMS.filter(podeVer) : [];

    const expanded = !collapsed || hovered;
    const setHovered = onHover || (() => {});

    function initials() {
        const n = (user && user.nome) || 'MR';
        const parts = n.trim().split(/\s+/);
        return ((parts[0] || '').slice(0, 1) + (parts[1] || '').slice(0, 1) || 'MR').toUpperCase();
    }

    function doLogout() {
        logout();
        navigate('/');
    }

    return (
        <aside
            className="sidebar"
            data-collapsed={collapsed ? 'true' : 'false'}
            data-expanded={expanded ? 'true' : 'false'}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div className="sidebar-brand">
                <div className="brand-monogram"><BabitaB size={18}/></div>
                {expanded && (
                    <div className="brand-wordmark-wrap">
                        <div className="brand-wordmark" style={{ height: 18, color: 'var(--ink)' }}>
                            <BabitaLogo/>
                        </div>
                        <div className="brand-tag">Central · Interna</div>
                    </div>
                )}
            </div>

            <div className="nav-section">
                {expanded && <div className="nav-section-title">Operação</div>}
                {opItemsVisiveis.map(it => (
                    <NavLink
                        key={it.to}
                        to={it.to}
                        className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}
                        title={it.label}
                        end
                    >
                        <span className="nav-icon"><Icon name={it.icon} size={17}/></span>
                        {expanded && <span className="nav-label">{it.label}</span>}
                    </NavLink>
                ))}
            </div>

            {isTIUser && tiItemsVisiveis.length > 0 && (
                <div className="nav-section">
                    {expanded && (
                        <div className="nav-section-title">
                            <span>TI</span>
                            <span className="nav-chip">RESTRITO</span>
                        </div>
                    )}
                    <div className="nav-group" data-open={itOpen ? 'true' : 'false'}>
                        <button
                            type="button"
                            className={'nav-item' + (isTIPath ? ' active' : '')}
                            onClick={() => setItOpen(!itOpen)}
                            style={{ background: 'transparent', border: 'none', width: '100%', textAlign: 'left', fontFamily: 'inherit' }}
                        >
                            <span className="nav-icon"><Icon name="lock" size={17}/></span>
                            {expanded && <span className="nav-label">TI · Restrito</span>}
                            {expanded && <span className="nav-chev"><Icon name="chev" size={13}/></span>}
                        </button>
                        {(itOpen || isTIPath) && (
                            <div className="nav-group-children">
                                {tiItemsVisiveis.map(it => (
                                    <NavLink
                                        key={it.to}
                                        to={it.to}
                                        className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}
                                        title={it.label}
                                    >
                                        <span className="nav-icon"><Icon name={it.icon} size={15}/></span>
                                        {expanded && <span className="nav-label">{it.label}</span>}
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {user && (
                <div className="sidebar-foot">
                    <div className="sidebar-foot-user" onClick={() => navigate('/Configuracoes')} title={user.nome}>
                        <div className="avatar">{initials()}</div>
                        {expanded && (
                            <div className="sidebar-foot-text">
                                <div className="name">{user.nome}</div>
                                <div className="role">{user.cargo || user.setor || ''}</div>
                            </div>
                        )}
                    </div>
                    <button className="sidebar-logout" onClick={doLogout} title="Sair">
                        <Icon name="logout" size={16}/>
                        {expanded && <span className="nav-label">Sair</span>}
                    </button>
                </div>
            )}
        </aside>
    );
}
